import { z } from 'zod';
import type { INowModel } from '@/types/now';
import { requireFields, yesNoRequired } from '@utils/form-schema';

const NAME_REGEX = /^[A-Za-z]+(?:['-][A-Za-z]+)*(?:\s+[A-Za-z]+(?:['-][A-Za-z]+)*)*\s*$/;
const NAME_ERROR = 'Name can only contain letters, hyphen, and/or apostrophe, no spaces';
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const ADDRESS_REGEX = /^[a-zA-Z0-9',\-. ]{1,256}$/;
const ADDRESS_ERROR = 'No special symbols allowed. Must contain English letters only, limit 256';
// The postal code is only asked of US residents, and api/register/now rejects anything
// that is not exactly 5 digits.
const POSTAL_CODE_REGEX = /^[0-9]{5}$/;

export const createNowFormSchema = (model: Pick<INowModel, 'isDashLiverAvailable'>) => {
    return z
        .object({
            firstName: z.string().min(1, 'Required').regex(NAME_REGEX, NAME_ERROR),
            lastName: z.string().min(1, 'Required').regex(NAME_REGEX, NAME_ERROR),
            isResidingInUsa: yesNoRequired,
            countryOfResidenceId: z.string(),
            internationalPhoneNumber: z.string(),
            addressLine1: z.string(),
            city: z.string(),
            state: z.string(),
            postalCode: z.string(),
            mobileAuthPhone: z.string(),
            email: z.string().min(1, 'Required').max(64, 'Too Long').regex(EMAIL_REGEX, 'Invalid'),
            donationType: z.string(),
            agreedToHipaa: z.boolean().nullable(),
            connectToDonorIsDesired: yesNoRequired,
        })
        .superRefine((data, ctx) => {
            const required = (field: keyof typeof data, message = 'Required') => {
                ctx.addIssue({ code: 'custom', path: [field], message });
            };

            if (data.isResidingInUsa === '1') {
                if (!data.addressLine1) {
                    required('addressLine1');
                } else if (!ADDRESS_REGEX.test(data.addressLine1)) {
                    required('addressLine1', ADDRESS_ERROR);
                }

                requireFields(ctx, data, 'city', 'state');

                if (!data.postalCode) {
                    required('postalCode');
                } else if (!POSTAL_CODE_REGEX.test(data.postalCode)) {
                    required('postalCode', 'Use 5 digit postal');
                }

                if (!data.mobileAuthPhone) {
                    required('mobileAuthPhone');
                } else if (!/^\d{10}$/.test(data.mobileAuthPhone)) {
                    required('mobileAuthPhone', 'Invalid');
                }
            }

            if (data.isResidingInUsa === '0') {
                requireFields(ctx, data, 'countryOfResidenceId');

                if (!data.internationalPhoneNumber) {
                    required('internationalPhoneNumber');
                } else if (data.internationalPhoneNumber.replace(/\D/g, '').length < 10) {
                    required('internationalPhoneNumber', 'Must be at least 10 digits');
                }
            }

            if (model.isDashLiverAvailable) {
                requireFields(ctx, data, 'donationType');
            }

            if (data.agreedToHipaa !== true) {
                required('agreedToHipaa');
            }
        });
};
